import React, { useEffect, useState } from 'react';
import { Modal, Button, Form, Row, Col, Spinner } from 'react-bootstrap';
import PropTypes from 'prop-types';
import useToken from 'hooks/useToken';
import { useToast } from 'context/providers/ToastProvider';

/**
 * ModalEditarUsuarioAkasiaPOS
 * Modal para editar un usuario Akasia POS existente
 * Solo permite editar: usuario, correo y contraseña
 * No afecta suscripciones, órdenes ni licencias (26 Dic 2025)
 */
const ModalEditarUsuarioAkasiaPOS = ({ show, onHide, usuario, onUpdated }) => {
    const [saving, setSaving] = useState(false);
    const [values, setValues] = useState({
        vUsuario: '',
        vEmail: '',
        vContrasena: ''
    });

    const { token } = useToken();
    const { showErrorToast, showSuccessToast } = useToast();

    useEffect(() => {
        if (show && usuario) {
            setValues({
                vUsuario: usuario.vUsername || '',
                vEmail: usuario.vEmail || '',
                vContrasena: usuario.vPassword || ''
            });
        } else {
            setValues({ vUsuario: '', vEmail: '', vContrasena: '' });
        }
    }, [show, usuario]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setValues({ ...values, [name]: value });
    };

    const handleSave = async () => {
        // Validaciones básicas
        if (!values.vUsuario) return showErrorToast('Usuario es obligatorio');
        if (!values.vEmail) return showErrorToast('Correo es obligatorio');
        if (!values.vContrasena) return showErrorToast('Contraseña es obligatoria');

        // Validar que email y contraseña no tengan espacios
        if (/\s/.test(values.vContrasena)) return showErrorToast('La contraseña no debe contener espacios');
        if (/\s/.test(values.vEmail)) return showErrorToast('El correo no debe contener espacios');

        // Validación básica de formato de correo
        const emailRe = /^\S+@\S+\.\S+$/;
        if (!emailRe.test(values.vEmail)) return showErrorToast('Correo inválido. Formato mínimo: x@x.x');

        setSaving(true);
        try {
            const payload = {
                idPosUser: usuario.idPosUser,
                vUsername: values.vUsuario,
                vEmail: values.vEmail,
                vPassword: values.vContrasena
            };

            const res = await fetch('/api/UsuariosAkasiaPOS/ActualizarUsuario', {
                method: 'POST',
                headers: { 
                    'Content-Type': 'application/json', 
                    'Authorization': `Bearer ${token}` 
                },
                body: JSON.stringify(JSON.stringify(payload))
            });

            const data = await res.json();
            if (data && data.iResponseType === 1) {
                showSuccessToast('Usuario POS actualizado correctamente.');
                onUpdated && onUpdated();
            } else {
                showErrorToast(data?.vMessage || 'No se pudo actualizar el usuario');
            }
        } catch (err) {
            showErrorToast('Error al actualizar usuario');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal show={show} onHide={onHide} size="md" backdrop="static" centered>
            <Modal.Header closeButton>
                <Modal.Title>
                    <img src="/img/editar.png" alt="Editar" className="me-2" style={{ width: '24px', height: '24px' }} />
                    Editar Usuario Akasia POS
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Row>
                    <Col>
                        <Form.Group className="mb-3">
                            <Form.Label>Usuario</Form.Label>
                            <Form.Control
                                name="vUsuario"
                                value={values.vUsuario}
                                onChange={handleChange}
                                placeholder="Nombre de usuario"
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Correo</Form.Label>
                            <Form.Control
                                name="vEmail"
                                value={values.vEmail}
                                onChange={handleChange}
                                placeholder="correo@ejemplo.com"
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Contraseña</Form.Label>
                            <Form.Control
                                name="vContrasena"
                                value={values.vContrasena}
                                onChange={handleChange}
                                placeholder="Contraseña"
                            />
                        </Form.Group>
                    </Col>
                </Row>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={onHide} disabled={saving}>
                    Cerrar
                </Button>
                <Button variant="primary" onClick={handleSave} disabled={saving}>
                    {saving ? (
                        <>
                            <Spinner animation="border" size="sm" className="me-2" />
                            Guardando...
                        </>
                    ) : (
                        'Guardar Cambios'
                    )}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

ModalEditarUsuarioAkasiaPOS.propTypes = {
    show: PropTypes.bool,
    onHide: PropTypes.func,
    usuario: PropTypes.shape({
        idPosUser: PropTypes.number,
        vUsername: PropTypes.string,
        vEmail: PropTypes.string,
        vPassword: PropTypes.string
    }),
    onUpdated: PropTypes.func
};

export default ModalEditarUsuarioAkasiaPOS;
